import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import MessageModal from 'components/Modals/MessageModal'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import { useUTMTracking } from 'hooks/useUTMTracking'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { useLocation, useNavigate } from 'react-router-dom'
import swal from 'sweetalert'
import { getInvestorAccountType, getModifiedUrlWithStep, getOnboardingType } from 'utils'
import { consoleLog } from 'utils/simpleLogger'
import CompanyLogo from '../../assets/afbc.png'
import TrustIcon from '../../assets/images/bank-building.svg'
import CompanyIcon from '../../assets/images/company-building.svg'
import IndividualIcon from '../../assets/images/noto_man-beard.svg'
import MoreThanOneIndividualIcon from '../../assets/images/noto_people-holding-hands.svg'
import Side2 from '../../assets/investing-signup-images/preamble.jpg'
import Button from '../../components/Button'
import LoaderModal from '../../components/Modals/LoaderModal'
import Stepper from '../../components/Stepper'
import { useRiseUpAPIV1 } from '../../hooks/useRiseUpAPIV1'
import { IFormProps, InvestingPersonEnum, RetirementAccountEnum } from '../../interfaces'
import { returnInvestmentType } from './InvestorPersonalInfo'

const enabledOptions = [
  InvestingPersonEnum.FOR_MYSELF,
  InvestingPersonEnum.JOINTLY,
  InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY,
  InvestingPersonEnum.RETIREMENT,
]

const InvestingPerson = (props: IFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { updateOnboardProgress, onboardUserEmail, updateOnboardingType } = useRiseUpAPIV1()
  const sendDataToGTM = useGTMDispatch()
  const location = useLocation()
  const { errorMessage, setErrorMessage } = useErrorMessage()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()
  const navigate = useNavigate()

  const handleInvestingPerson = (value: InvestingPersonEnum) => {
    props.setInvestorType(value)
    props.setValue('investingProfile.option', value)
    props.setValue('addressInfo.country', 'USA')

    if (value !== InvestingPersonEnum.RETIREMENT) {
      props.unregister('investingProfile.retirementAccountType')
    }
  }

  const handleRetiremenAccntType = (value: string) => {
    props.setRetirementAccntType(value)
    props.setValue('investingProfile.retirementAccountType', value)
    props.setValue('investingProfile.accountType', getInvestorAccountType(value))
  }

  const handleSubmit = async () => {
    setIsLoading(true)

    const formattedUTMParams = getFormattedUTMParams()
    const pathname = location.pathname
    const currentParams = queryString.parse(location.search)
    const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'investor_personal_info')

    const email = props.getValues('email')
    const investingProfileOption = props.getValues('investingProfile.option')
    const appliedOnboardingGroupLink = currentParams.appliedOnboardingGroupLink as string

    const onboardingType = getOnboardingType(
      investingProfileOption !== InvestingPersonEnum.RETIREMENT ? investingProfileOption : props.retirementAccntType,
    )
    const nextStep = investingProfileOption === InvestingPersonEnum.JOINTLY ? '2a' : '3'
    if (props.getValues('investingProfile.accountType') === '') {
      props.setValue('investingProfile.accountType', getInvestorAccountType(investingProfileOption))
    }
    consoleLog('INVESTOR ACCOUNT TYPE:', props.getValues('investingProfile.accountType'))

    if (props.getValues('onboardingId')) {
      try {
        const updateOnboardingTypeAsync = async (groupLink: string | undefined) => {
          if (groupLink) {
            return await updateOnboardingType(email, onboardingType, groupLink)
          } else {
            return await updateOnboardingType(email, onboardingType)
          }
        }

        await updateOnboardingTypeAsync(appliedOnboardingGroupLink)

        const data = {
          ...props.getValues(),
          lastCompletedStage: props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY ? '2a' : '3',
        }

        await updateOnboardProgress(data)
        props.nextStep(nextStep)
      } catch (error: any) {
        // catch the duplicate errors
        setIsLoading(false)
        if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
          setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
          return
        }
        if (error.response.data === RiseUpErrors.DuplicateSSN) {
          setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
          return
        }
        console.error(error)
      }
    } else {
      try {
        let onboardUserEmailRes

        const onboardUserEmailAsync = async (groupLink: string | undefined) => {
          if (groupLink) {
            return await onboardUserEmail(email, onboardingType, groupLink)
          } else {
            return await onboardUserEmail(email, onboardingType)
          }
        }
        onboardUserEmailRes = await onboardUserEmailAsync(appliedOnboardingGroupLink)

        if (onboardUserEmailRes.status === 201) {
          props.setValue('onboardingId', onboardUserEmailRes.data.onboardingId)
          const data = {
            ...props.getValues(),
            lastCompletedStage: props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY ? '2a' : '3',
          }

          const updateOnboardProgressRes = await updateOnboardProgress(data)

          if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
            sendDataToGTM({
              event: 'funnel_step',
              step_title: 'INVESTOR_TYPE',
              ...gtmData,
              ...formattedUTMParams,
            })
            navigate(updatedUrl)
            props.nextStep(nextStep)
          }
        }
      } catch (error) {
        consoleLog('Investing person submit error', error)
        ReactGA.event({
          category: 'Investing person page',
          action: `Investing person page error`,
          label: JSON.stringify(error),
        })
        console.log(error)
        const errorResponse =
          ((error as any).response.data._error as string) || ((error as any).response.data as string)

        if (
          errorResponse.includes('Invalid Onboarding Group Link') ||
          errorResponse.includes('Onboarding Group Link is invalid')
          // ((error as any).response.data._error as string).includes('Onboarding Group Link is invalid') ||
          // ((error as any).response.data as string).includes('Invalid Onboarding Group Link')
        ) {
          setIsLoading(false)
          setError('Invalid Onboarding Group Link')
          setErrorMessage('Invalid Onboarding Group Link')
        } else {
          setIsLoading(false)
          swal({
            title: 'Error!',
            text: 'Please check if your details are valid and correct. If error persists, contact us.',
            icon: 'error',
          })
        }
      }
    }
  }

  useEffect(() => {
    props.setValue('investingProfile.itemId', 'FIRST_INVESTMENT')
  }, []) // eslint-disable-line

  useEffect(() => {
    const investingOption = props.getValues('investingProfile.option')
    const jointAccountType = props.getValues('investingProfile.jointAccountType')
    const retirementAccountType = props.getValues(' investingProfile.retirementAccountType')
    props.setInvestorType(investingOption)
    props.setValue('addressInfo.country', 'USA')
    props.setValue('investingProfile.itemId', 'FIRST_INVESTMENT')

    switch (investingOption) {
      case InvestingPersonEnum.JOINTLY:
        props.setValue('secondary.addressInfo.country', 'USA')
        props.unregister('entityDetails.countryOfFormation')
        props.setValue('investingProfile.accountType', getInvestorAccountType(jointAccountType))
        break

      case InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY:
      case InvestingPersonEnum.RETIREMENT:
        props.setValue('entityDetails.countryOfFormation', 'USA')
        props.unregister('secondary.addressInfo.country')
        props.setValue(
          'investingProfile.accountType',
          getInvestorAccountType(
            investingOption === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
              ? InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
              : retirementAccountType,
          ),
        )
        break

      case InvestingPersonEnum.FOR_MYSELF:
        props.unregister('entityDetails.countryOfFormation')
        props.unregister('secondary.addressInfo.country')
        props.setValue('investingProfile.accountType', getInvestorAccountType(investingOption))
        break

      default:
        break
    }
  }, [props.getValues('investingProfile.option')]) // eslint-disable-line

  useEffect(() => {
    if (props.getValues('investingProfile.retirementAccountType')) {
      handleRetiremenAccntType(props.getValues('investingProfile.retirementAccountType'))
    } else {
      handleRetiremenAccntType('')
    }
  }, [props.getValues('investingProfile.retirementAccountType')]) // eslint-disable-line

  // useEffect(() => {
  //   sendDataToGTM({
  //     event: 'funnel_step',
  //     step_title: 'INVESTOR_TYPE',
  //     ...gtmData,
  //   })
  // }, [])

  return (
    <>
      <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
        <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[35%]">
          <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
            <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
          </div>
          <div className="relative hidden flex-grow md:flex">
            <img className="flex h-screen w-full" src={Side2} alt={`${Side2}`} />
          </div>
        </div>

        <div className="flex flex-col p-6 sm:w-[100%] md:h-screen md:w-[65%] md:overflow-y-scroll md:p-10">
          <div className="md:my-3">
            <Stepper current={props.currentStage} />
          </div>

          <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
            Invest in Armed Forces Brewing
          </div>

          <div className="lg:text-1xl pb-12 text-left text-lg font-medium text-gray-900 lg:pb-8">
            I am investing as...
          </div>

          <div className="grid gap-8 px-12 py-6 md:flex md:px-0 md:py-0">
            <div
              style={{ border: '2px solid #E5E7EB' }}
              className={`flex min-h-[96px] flex-col items-center justify-center rounded-lg border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] lg:w-1/4 lg:border-2 ${
                props.investorType === InvestingPersonEnum.FOR_MYSELF
                  ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl'
                  : ''
              } ${!enabledOptions.includes(InvestingPersonEnum.FOR_MYSELF) && 'opacity-50'}`}
              onClick={() => {
                if (enabledOptions.includes(InvestingPersonEnum.FOR_MYSELF)) {
                  handleInvestingPerson(InvestingPersonEnum.FOR_MYSELF)
                }
              }}
            >
              <img src={IndividualIcon} alt={`${IndividualIcon}`} className="min-w-[92px]" />

              <div
                className={`mt-4 text-base ${
                  props.investorType === InvestingPersonEnum.FOR_MYSELF ? 'font-bold' : 'font-normal'
                } 
                `}
              >
                Individual
              </div>
            </div>

            <div
              style={{ border: '2px solid #E5E7EB' }}
              className={`flex min-h-[96px] flex-col items-center justify-center rounded-lg border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] lg:w-1/4 lg:border-2 ${
                props.investorType === InvestingPersonEnum.JOINTLY
                  ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl'
                  : ''
              } ${!enabledOptions.includes(InvestingPersonEnum.JOINTLY) && 'opacity-50'}`}
              onClick={() => {
                if (enabledOptions.includes(InvestingPersonEnum.JOINTLY)) {
                  handleInvestingPerson(InvestingPersonEnum.JOINTLY)
                }
              }}
            >
              <img src={MoreThanOneIndividualIcon} alt={`${MoreThanOneIndividualIcon}`} className="min-w-[92px]" />

              <div
                className={`text-basee mt-4 ${
                  props.investorType === InvestingPersonEnum.JOINTLY ? 'font-bold' : 'font-normal'
                } `}
              >
                Joint
              </div>
            </div>

            <div
              style={{ border: '2px solid #E5E7EB' }}
              className={`flex min-h-[96px] flex-col items-center justify-center rounded-lg border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] lg:w-1/4 lg:border-2 ${
                props.investorType === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
                  ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl'
                  : ''
              } ${!enabledOptions.includes(InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY) && 'opacity-50'}`}
              onClick={() => {
                if (enabledOptions.includes(InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY)) {
                  handleInvestingPerson(InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY)
                }
              }}
            >
              <img src={CompanyIcon} alt={`${CompanyIcon}`} className="min-w-[92px]" />

              <div
                className={`mt-4 text-base ${
                  props.investorType === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY ? 'font-bold' : 'font-normal'
                }`}
              >
                Company
              </div>
            </div>

            <div
              style={{ border: '2px solid #E5E7EB' }}
              className={`flex min-h-[96px] flex-col items-center justify-center rounded-lg border-[#E5E7EB] p-7 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] lg:w-1/4 lg:border-2 ${
                props.investorType === InvestingPersonEnum.RETIREMENT
                  ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl'
                  : ''
              }${!enabledOptions.includes(InvestingPersonEnum.RETIREMENT) && 'opacity-50'}`}
              onClick={() => {
                if (enabledOptions.includes(InvestingPersonEnum.RETIREMENT)) {
                  handleInvestingPerson(InvestingPersonEnum.RETIREMENT)
                }
              }}
            >
              <img className="min-w-[92px" src={TrustIcon} alt={`${TrustIcon}`} />

              <div
                className={`mt-4 text-base ${
                  props.investorType === InvestingPersonEnum.RETIREMENT ? 'font-bold text-[#FFFFFF]' : 'font-normal'
                }`}
              >
                Trust or Retirement Account
              </div>
            </div>
          </div>

          {props.investorType === InvestingPersonEnum.RETIREMENT && (
            <div className="flex flex-col gap-8 sm:grid lg:flex">
              <div className="text-basic pt-4 text-left font-medium text-gray-900 md:text-xl">
                I want to invest through a...
              </div>
              <div className="flex w-full flex-col gap-4 sm:flex-row">
                <div
                  style={{ border: '2px solid #E5E7EB' }}
                  className={`mr-4 flex w-full flex-row items-center justify-center rounded-lg border-[#E5E7EB] py-4 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] lg:border-2 ${
                    props.retirementAccntType === RetirementAccountEnum.TRUST_ACCOUNT
                      ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl'
                      : ''
                  }`}
                  onClick={() => {
                    handleRetiremenAccntType(RetirementAccountEnum.TRUST_ACCOUNT)
                  }}
                >
                  <div className="mr-4 w-9 rounded-full bg-[#A1A8B7] p-1 text-xl font-bold">1</div>
                  <div
                    className={`text-base ${
                      props.retirementAccntType === RetirementAccountEnum.TRUST_ACCOUNT ? 'font-bold' : 'font-normal'
                    }`}
                  >
                    Trust
                  </div>
                </div>
                <div
                  style={{ border: '2px solid #E5E7EB' }}
                  className={`mr-4 flex w-full flex-row items-center justify-center rounded-lg border-[#E5E7EB] py-4 text-center hover:cursor-pointer sm:border-solid sm:border-[#DDD] lg:border-2  ${
                    props.retirementAccntType === RetirementAccountEnum.RETIREMENT_ACCOUNT
                      ? 'border-[#1A56DB] bg-[#1A56DB] text-[#FFFFFF] shadow-xl'
                      : ''
                  }`}
                  onClick={() => {
                    handleRetiremenAccntType(RetirementAccountEnum.RETIREMENT_ACCOUNT)
                  }}
                >
                  <div className="mr-4 w-10 rounded-full bg-[#A1A8B7] p-1 text-xl font-bold">2</div>
                  <div
                    className={`text-bas ${
                      props.retirementAccntType === RetirementAccountEnum.RETIREMENT_ACCOUNT
                        ? 'font-bold'
                        : 'font-normal'
                    }`}
                  >
                    Retirement Account
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="w-full justify-center gap-3 py-10 sm:block lg:flex">
            <div className="flex flex-col sm:w-[100%] lg:w-[20%]">
              <Button
                btnName={'Back'}
                onClickHandler={() => {
                  props.setValue('fromSecondPage', true)
                  props.previousStep('1')
                }}
              />
            </div>
            <div className="flex flex-col sm:w-[100%] lg:w-[80%]">
              <Button
                btnName={`Next: Investor Info ${returnInvestmentType(props?.investorType || '')}`}
                btnType={'button'}
                onClickHandler={handleSubmit}
                disabled={
                  props.investorType === undefined ||
                  (props.investorType === InvestingPersonEnum.RETIREMENT && !props.retirementAccntType)
                }
              />
            </div>
          </div>
        </div>
      </div>

      <LoaderModal isVisible={isLoading} />
      <MessageModal
        isVisible={error !== '' || errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
          }
          setErrorMessage('')
          setError('')
        }}
        onCancel={() => {
          setErrorMessage('')
        }}
        title={'Error'}
        description={errorMessage || 'Error'}
        type={'error'}
        btn2={'Ok'}
      />
    </>
  )
}

export default InvestingPerson
